"use client";

import VillageModal from "@/components/modals/VillageModal";
import {
    CHECKIN_RAID_SESSION_MUTATION,
    CHECKIN_SENTRY_SESSION_MUTATION,
    CHECKIN_SESSION_MUTATION,
    CLAIM_CLAN_SPOILS_SESSION_MUTATION,
    CLAIM_DRINK_SESSION_MUTATION,
    CLAIM_HYPE_TRAIN_LOOT_SESSION_MUTATION,
    CLAIM_SPOILS_SESSION_MUTATION,
    REDEEM_WHEEL_OF_HAMINGJA_SESSION_MUTATION,
    REDEEM_WHEEL_OF_HAMINGJA_TOKEN_MUTATION,
} from "@/graphql/mutations/sessionMutations";
import {
    ACTIVE_CHECKIN_SESSION_QUERY,
    ACTIVE_CLAN_SPOILS_SESSION_QUERY,
    ACTIVE_DRINK_SESSION_QUERY,
    ACTIVE_HYPE_TRAIN_LOOT_SESSION_QUERY,
    ACTIVE_RAID_SESSION_QUERY,
    ACTIVE_SENTRY_SESSION_QUERY,
    ACTIVE_SPOILS_SESSION_QUERY,
    ACTIVE_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_QUERY,
    ACTIVE_WHEEL_OF_HAMINGJA_SESSION_QUERY,
} from "@/graphql/queries/sessionQueries";
import {
    ACTIVE_CHECKIN_SESSION_SUBSCRIPTION,
    ACTIVE_CLAN_SPOILS_SESSION_SUBSCRIPTION,
    ACTIVE_DRINK_SESSION_SUBSCRIPTION,
    ACTIVE_HYPE_TRAIN_LOOT_SESSION_SUBSCRIPTION,
    ACTIVE_RAID_SESSION_SUBSCRIPTION,
    ACTIVE_SENTRY_SESSION_SUBSCRIPTION,
    ACTIVE_SPOILS_SESSION_SUBSCRIPTION,
    ACTIVE_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_SUBSCRIPTION,
    ACTIVE_WHEEL_OF_HAMINGJA_SESSION_SUBSCRIPTION,
} from "@/graphql/subscriptions/sessionSubscriptions";
import { classNamesExtended } from "@/helpers/common/classNames";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import React, { useState } from "react";
import FeedbackModal from "./FeedbackModal";

type ButtonConfig = {
    key: string;
    activeLabel: string;
    inactiveLabel: string;
    active: boolean;
    loading: boolean;
    onClick: () => void;
    successMessage?: React.ReactNode;
    errorMessage?: React.ReactNode;
    buttonColor: string;
    textColor: string;
};

const StreamDeckPanel: React.FC = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalMessage, setModalMessage] = useState<React.ReactNode>("");
    const [modalIsError, setModalIsError] = useState(false);

    const [wohConfirmOpen, setWohConfirmOpen] = useState(false);

    const showModal = (
        title: string,
        message: React.ReactNode,
        isError = false,
    ) => {
        setModalTitle(title);
        setModalMessage(message);
        setModalIsError(isError);
        setModalOpen(true);
    };

    const handleMutation = async ({
        mutationFn,
        refetchFn,
        successMessage,
        errorMessage,
    }: {
        mutationFn: () => Promise<any>;
        refetchFn: () => void;
        successMessage?: React.ReactNode;
        errorMessage?: React.ReactNode;
    }) => {
        try {
            await mutationFn();
            refetchFn();
            if (successMessage) {
                showModal("Skál!", successMessage, false);
            }
        } catch (err: any) {
            const fallback = errorMessage || "Oops, something went wrong.";
            showModal("Oops!", err?.message || fallback, true);
        }
    };

    // Queries + Mutations with real-time subscriptions
    const {
        data: sentryData,
        loading: sentryLoading,
        refetch: refetchSentry,
    } = useQuery(ACTIVE_SENTRY_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_SENTRY_SESSION_SUBSCRIPTION, {
        onData: (result) => {
            console.debug(
                "[StreamDeckPanel] Sentry subscription data received:",
                result.data,
            );
            refetchSentry();
        },
    });
    const [checkInSentry, { loading: checkInLoading }] = useMutation(
        CHECKIN_SENTRY_SESSION_MUTATION,
    );

    const {
        data: spoilsData,
        loading: spoilsLoading,
        refetch: refetchSpoils,
    } = useQuery(ACTIVE_SPOILS_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_SPOILS_SESSION_SUBSCRIPTION, {
        onData: () => refetchSpoils(),
    });
    const [claimSpoils, { loading: claimSpoilsLoading }] = useMutation(
        CLAIM_SPOILS_SESSION_MUTATION,
    );

    const {
        data: drinkData,
        loading: drinkLoading,
        refetch: refetchDrink,
    } = useQuery(ACTIVE_DRINK_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_DRINK_SESSION_SUBSCRIPTION, {
        onData: () => refetchDrink(),
    });
    const [claimDrink, { loading: claimDrinkLoading }] = useMutation(
        CLAIM_DRINK_SESSION_MUTATION,
    );

    const {
        data: clanSpoilsData,
        loading: clanSpoilsLoading,
        refetch: refetchClanSpoils,
    } = useQuery(ACTIVE_CLAN_SPOILS_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_CLAN_SPOILS_SESSION_SUBSCRIPTION, {
        onData: () => refetchClanSpoils(),
    });
    const [claimClanSpoils, { loading: claimClanSpoilsLoading }] = useMutation(
        CLAIM_CLAN_SPOILS_SESSION_MUTATION,
    );

    const {
        data: checkinData,
        loading: checkinLoading,
        refetch: refetchCheckin,
    } = useQuery(ACTIVE_CHECKIN_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_CHECKIN_SESSION_SUBSCRIPTION, {
        onData: () => refetchCheckin(),
    });
    const [checkIn, { loading: checkInSessionLoading }] = useMutation(
        CHECKIN_SESSION_MUTATION,
    );

    const {
        data: raidData,
        loading: raidLoading,
        refetch: refetchRaid,
    } = useQuery(ACTIVE_RAID_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_RAID_SESSION_SUBSCRIPTION, {
        onData: () => refetchRaid(),
    });
    const [checkInRaid, { loading: checkInRaidLoading }] = useMutation(
        CHECKIN_RAID_SESSION_MUTATION,
    );

    const {
        data: hypeData,
        loading: hypeLoading,
        refetch: refetchHype,
    } = useQuery(ACTIVE_HYPE_TRAIN_LOOT_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_HYPE_TRAIN_LOOT_SESSION_SUBSCRIPTION, {
        onData: () => refetchHype(),
    });
    const [claimHypeTrainLoot, { loading: claimHypeTrainLootLoading }] =
        useMutation(CLAIM_HYPE_TRAIN_LOOT_SESSION_MUTATION);

    const {
        data: wheelData,
        loading: wheelLoading,
        refetch: refetchWheel,
    } = useQuery(ACTIVE_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_SUBSCRIPTION, {
        onData: () => refetchWheel(),
    });
    const [redeemWheelOfHamingjaToken, { loading: redeemWheelLoading }] =
        useMutation(REDEEM_WHEEL_OF_HAMINGJA_TOKEN_MUTATION);

    const {
        data: wheelSessionData,
        loading: wheelSessionLoading,
        refetch: refetchWheelSession,
    } = useQuery(ACTIVE_WHEEL_OF_HAMINGJA_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_WHEEL_OF_HAMINGJA_SESSION_SUBSCRIPTION, {
        onData: () => refetchWheelSession(),
    });
    const [
        redeemWheelOfHamingjaSession,
        { loading: redeemWheelSessionLoading },
    ] = useMutation(REDEEM_WHEEL_OF_HAMINGJA_SESSION_MUTATION);

    const buttons: ButtonConfig[] = [
        {
            key: "sentry",
            inactiveLabel: "SENTRY",
            activeLabel: "SENTRY!",
            active:
                sentryData?.activeSentrySessionCheckin?.hasActiveSession &&
                !sentryData?.activeSentrySessionCheckin?.checkedIn,
            loading: sentryLoading || checkInLoading,
            onClick: () =>
                handleMutation({
                    mutationFn: () => checkInSentry(),
                    refetchFn: refetchSentry,
                    successMessage:
                        "+1 Hour Sentry Time. Thank you for keeping watch!",
                    errorMessage: "You missed the Sentry check-in window.",
                }),
            successMessage: "+1 Hour Sentry Time. Thank you for keeping watch!",
            errorMessage: "You missed the Sentry check-in window.",
            buttonColor: "#4B03DD",
            textColor: "#FFCC33",
        },
        {
            key: "checkin",
            inactiveLabel: "CHECK-IN",
            activeLabel: "CHECK-IN!",
            active:
                checkinData?.activeCheckinSessionStatus?.hasActiveSession &&
                !checkinData?.activeCheckinSessionStatus?.checkedIn,
            loading: checkinLoading || checkInSessionLoading,
            onClick: () =>
                handleMutation({
                    mutationFn: () => checkIn(),
                    refetchFn: refetchCheckin,
                    successMessage:
                        "You’ve arrived at the Longhouse. Welcome, Viking! Skál!",
                    errorMessage: "You missed the check-in window.",
                }),
            successMessage:
                "You’ve arrived at the Longhouse. Welcome, Viking! Skál!",
            errorMessage: "You missed the check-in window.",
            buttonColor: "#961104",
            textColor: "#FFCC33",
        },
        {
            key: "wheel",
            inactiveLabel: "REDEEM WOH TOKEN",
            activeLabel: "REDEEM WOH TOKEN!",
            active:
                wheelData?.activeWheelOfHamingjaRedemptionSessionStatus
                    ?.hasActiveSession &&
                wheelData?.activeWheelOfHamingjaRedemptionSessionStatus
                    ?.hasAvailableTokens,
            loading: wheelLoading || redeemWheelLoading,
            onClick: () => setWohConfirmOpen(true),
            successMessage: "You have redeemed a Wheel of Hamingja token!",
            errorMessage: "You have no available tokens to redeem.",
            buttonColor: "#1A855C",
            textColor: "#FFCC33",
        },
        {
            key: "lucky",
            inactiveLabel: "WHEEL OF HAMINGJA",
            activeLabel: "LUCKY!",
            active:
                wheelSessionData?.activeWheelOfHamingjaSessionQueryStatus
                    ?.hasActiveSession &&
                !wheelSessionData?.activeWheelOfHamingjaSessionQueryStatus
                    ?.claimed,
            loading: wheelSessionLoading || redeemWheelSessionLoading,
            onClick: () =>
                handleMutation({
                    mutationFn: () => redeemWheelOfHamingjaSession(),
                    refetchFn: refetchWheelSession,
                    successMessage:
                        "You’ve claimed the next tier of the Tag of Harminya. Lucky you!",
                    errorMessage:
                        "Something went wrong with the Wheel of Hamingja.",
                }),
            successMessage:
                "You’ve claimed the next tier of the Tag of Harminya. Lucky you!",
            errorMessage: "Something went wrong with the Wheel of Hamingja.",
            buttonColor: "#FFCC33",
            textColor: "#1A855C",
        },
        {
            key: "spoils",
            inactiveLabel: "SPOILS",
            activeLabel: "CLAIM!",
            active:
                spoilsData?.activeSpoilsSessionStatus?.hasActiveSession &&
                !spoilsData?.activeSpoilsSessionStatus?.hasRedeemed,
            loading: spoilsLoading || claimSpoilsLoading,
            onClick: () =>
                handleMutation({
                    mutationFn: () => claimSpoils(),
                    refetchFn: refetchSpoils,
                    successMessage:
                        "Thank you for your aid in battle, warrior!",
                    errorMessage:
                        "You already claimed or missed the spoils window.",
                }),
            successMessage: "Thank you for your aid in battle, warrior!",
            errorMessage: "You already claimed or missed the spoils window.",
            buttonColor: "#FFCC33",
            textColor: "#000000",
        },
        {
            key: "drink",
            inactiveLabel: "DRINK",
            activeLabel: "SKÁL!",
            active:
                drinkData?.activeDrinkSessionStatus?.hasActiveSession &&
                !drinkData?.activeDrinkSessionStatus?.hasRedeemed,
            loading: drinkLoading || claimDrinkLoading,
            onClick: () =>
                handleMutation({
                    mutationFn: () => claimDrink(),
                    refetchFn: refetchDrink,
                    successMessage: "Raise your horn high, warrior! Skál!",
                    errorMessage:
                        "You already claimed or missed the drink window.",
                }),
            successMessage: "Raise your horn high, warrior! Skál!",
            errorMessage: "You already claimed or missed the drink window.",
            buttonColor: "#8B4513",
            textColor: "#FFCC33",
        },
        {
            key: "clanSpoils",
            inactiveLabel: "CLAN SPOILS",
            activeLabel: "CLAIM!",
            active:
                clanSpoilsData?.activeClanSpoilsSessionStatus
                    ?.hasActiveSession &&
                !clanSpoilsData?.activeClanSpoilsSessionStatus?.hasRedeemed,
            loading: clanSpoilsLoading || claimClanSpoilsLoading,
            onClick: () =>
                handleMutation({
                    mutationFn: () => claimClanSpoils(),
                    refetchFn: refetchClanSpoils,
                    successMessage:
                        "You have claimed in honor of your clan. Skál!",
                    errorMessage:
                        "You already claimed or missed the clan spoils window.",
                }),
            successMessage: "You have claimed in honor of your clan. Skál!",
            errorMessage:
                "You already claimed or missed the clan spoils window.",
            buttonColor: "#FFCC33",
            textColor: "#000000",
        },
        {
            key: "raid",
            inactiveLabel: "RAID",
            activeLabel: "GET IN THE BOAT!",
            active:
                raidData?.activeRaidSessionStatus?.hasActiveSession &&
                !raidData?.activeRaidSessionStatus?.checkedIn,
            loading: raidLoading || checkInRaidLoading,
            onClick: () =>
                handleMutation({
                    mutationFn: () => checkInRaid(),
                    refetchFn: refetchRaid,
                    successMessage:
                        "Sharpen your steel, bolster your shields, get ready to raid!",
                    errorMessage: "You missed the raid check-in window.",
                }),
            successMessage:
                "Sharpen your steel, bolster your shields, get ready to raid!",
            errorMessage: "You missed the raid check-in window.",
            buttonColor: "#092436",
            textColor: "#FFCC33",
        },
        {
            key: "hype",
            inactiveLabel: "HYPE TRAIN LOOT",
            activeLabel: "LOOT!",
            active:
                hypeData?.activeHypeTrainLootSession?.hasActiveSession &&
                !hypeData?.activeHypeTrainLootSession?.redeemed,
            loading: hypeLoading || claimHypeTrainLootLoading,
            onClick: async () => {
                try {
                    const res = await claimHypeTrainLoot();
                    refetchHype();

                    if (res?.data?.claimHypeTrainLootSession?.prize) {
                        const prize = res.data.claimHypeTrainLootSession.prize;
                        showModal(
                            "Skál!",
                            <>
                                You have found some epic loot on the shores of
                                the fjord! <br />
                                You found <strong>{prize}</strong>
                            </>,
                            false,
                        );
                    } else {
                        showModal("Skál!", "Loot claimed!", false);
                    }
                } catch (err: any) {
                    showModal(
                        "Oops!",
                        err?.message || "Could not claim hype loot.",
                        true,
                    );
                }
            },
            successMessage: "Loot claimed!",
            errorMessage: "Could not claim hype loot.",
            buttonColor: "#8600A8",
            textColor: "#FFCC33",
        },
    ];

    return (
        <>
            <div className="bg-brand_charcoal/60 grid w-full grid-cols-1 gap-4 rounded-xl p-6 sm:grid-cols-2 lg:grid-cols-3">
                {buttons.map((btn) => {
                    const isActive = btn.active && !btn.loading;
                    return (
                        <button
                            key={btn.key}
                            className={classNamesExtended(
                                "rounded-lg px-4 py-6 text-center text-xl font-semibold transition-colors duration-300",
                                {
                                    "cursor-pointer": isActive,
                                    "cursor-not-allowed": !isActive,
                                },
                            )}
                            style={{
                                backgroundColor: isActive
                                    ? btn.buttonColor
                                    : "#555",
                                color: isActive ? btn.textColor : "#ccc",
                            }}
                            disabled={!isActive}
                            onClick={btn.onClick}
                        >
                            {btn.loading
                                ? "Processing..."
                                : isActive
                                  ? btn.activeLabel
                                  : btn.inactiveLabel}
                        </button>
                    );
                })}
            </div>

            <FeedbackModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title={modalTitle}
                message={modalMessage}
                isError={modalIsError}
            />

            <VillageModal
                isOpen={wohConfirmOpen}
                onClose={() => setWohConfirmOpen(false)}
                onConfirm={() => {
                    setWohConfirmOpen(false);
                    handleMutation({
                        mutationFn: () => redeemWheelOfHamingjaToken(),
                        refetchFn: refetchWheel,
                        successMessage:
                            "You have redeemed a Wheel of Hamingja token!",
                        errorMessage: "You have no available tokens to redeem.",
                    });
                }}
                type="confirm"
                title="Redeem Token"
                message="Are you sure you want to redeem a Wheel of Hamingja token? This action cannot be undone."
                confirmText="Redeem"
                cancelText="Cancel"
            />
        </>
    );
};

export default StreamDeckPanel;
